import React from 'react'
import { useSelector } from 'react-redux'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import * as R from 'ramda'

import { getLocation } from 'ducks/router'
import { useDebounce } from 'hooks'
import { getOrm } from 'orm'

const getOrdering = ({ mapSortingPropToQueryParam }) =>
  R.reduce((acc, { id, desc }) => {
    const paramName = mapSortingPropToQueryParam[id]
    const prefix = desc ? '-' : ''

    return [...acc, `${prefix}${paramName}`]
  }, [])

const useTableState = ({
  mapSortingPropToQueryParam,
  search,
  filterQuery,
  defaultPageSize = 25,
}) => {
  const { isPending } = useSelector(getOrm) as any
  const { query = {} } = useSelector(getLocation) as any

  const debouncedSearch = useDebounce(search)

  const [sorting, setSorting] = React.useState(
    query?.ordering
      ? R.map(
          key => ({
            id: R.invertObj(mapSortingPropToQueryParam)[
              R.replace('-', '', key)
            ],
            desc: R.includes('-', key) ? true : false,
          }),
          typeof query?.ordering === 'string'
            ? [query?.ordering]
            : [...query?.ordering],
        )
      : [],
  )
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: Number(query?.offset / query?.limit) || 0,
    pageSize: Number(query?.limit) || defaultPageSize,
  })
  const [isFetching, setIsFetching] = React.useState(true)

  React.useEffect(() => {
    setPagination({ pageSize, pageIndex: 0 })
  }, [debouncedSearch, filterQuery, pageSize, setPagination])

  React.useEffect(() => {
    setIsFetching(true)
  }, [debouncedSearch, pageIndex, pageSize, setIsFetching])

  return {
    debouncedSearch,
    pageIndex,
    pageSize,
    sorting,
    isFetching,
    isPending,
    setIsFetching,
    setPagination,
    setSorting,
  }
}

export const useTable = ({
  columns,
  data,
  mapSortingPropToQueryParam,
  pageCount,
  rowSelection = {},
  defaultPageSize,
  search,
  filterQuery,
  fetchAction,
  updateQueriesAction,
  onRowSelectionChange,
}) => {
  const {
    debouncedSearch,
    pageIndex,
    pageSize,
    sorting,
    isFetching,
    isPending,
    setIsFetching,
    setPagination,
    setSorting,
  } = useTableState({
    mapSortingPropToQueryParam,
    search,
    filterQuery,
    defaultPageSize,
  })

  const timeoutId = React.useRef(null)

  const getRowId = React.useCallback(row => row?.id, [])

  const limit = pageSize
  const offset = pageIndex * limit

  const table = useReactTable({
    data: isFetching ? [] : data,
    columns: isFetching ? [] : columns,
    pageCount: Math.ceil(pageCount / pageSize),
    state: { pagination: { pageIndex, pageSize }, rowSelection, sorting },
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange: setPagination,
    onRowSelectionChange: (updater: any) =>
      onRowSelectionChange(updater(rowSelection)),
    onSortingChange: setSorting,
    debugTable: false,
    manualPagination: true,
    manualSorting: true,
  })

  const handleFetch = React.useCallback(() => {
    const ordering = getOrdering({ mapSortingPropToQueryParam })(sorting)

    fetchAction({
      limit,
      offset,
      ordering,
      search: Boolean(debouncedSearch) ? debouncedSearch : undefined,
      ...filterQuery,
    })
  }, [
    debouncedSearch,
    limit,
    mapSortingPropToQueryParam,
    offset,
    sorting,
    fetchAction,
    filterQuery,
  ])

  const handleQueries = React.useCallback(() => {
    const ordering = getOrdering({ mapSortingPropToQueryParam })(sorting)

    updateQueriesAction({
      limit,
      offset,
      ordering,
      search: Boolean(debouncedSearch) ? debouncedSearch : undefined,
      ...filterQuery,
    })
  }, [
    debouncedSearch,
    limit,
    mapSortingPropToQueryParam,
    offset,
    sorting,
    updateQueriesAction,
    filterQuery,
  ])

  React.useEffect(() => {
    setIsFetching(true)

    handleFetch()

    timeoutId.current = window.setTimeout(() => {
      setIsFetching(false)
    }, 750)

    return () => {
      window.clearTimeout(timeoutId.current)
    }
  }, [timeoutId, handleFetch, setIsFetching])

  React.useEffect(() => {
    handleQueries()
  }, [handleQueries])

  return {
    state: {
      pagination: {
        currentPage: table.getState().pagination.pageIndex + 1,
        pageCount: table.getPageCount(),
        perPage: table.getState().pagination.pageSize,
      },
      table: {
        headers: table.getAllColumns(),
        model: table.getRowModel(),
        rowSelection: table.getState().rowSelection,
        isAllRowsSelected: table.getIsAllRowsSelected(),
        isFetching: isFetching || isPending,
        isSomeRowsSelected: table.getIsSomeRowsSelected(),
      },
    },
    handlers: {
      pagination: {
        handlePage: pageIndex => table.setPageIndex(pageIndex),
        handlePageSize: event => table.setPageSize(event.target.value),
      },
      table: {
        toggleAllRowsSelectedHandler: table.getToggleAllRowsSelectedHandler(),
      },
    },
  }
}
